import React, { useContext, useEffect, useMemo, useState } from 'react';
import styles from '../../Games/components/AddCategoryForm.module.scss';
import { CloseRoundSmall, Input, Magnifier } from 'esa-ui-library';
import { Form } from '../../../components';
import { searchNestedTree, useStore } from '../../../../common';
import { PropertyType, TreeValueType } from 'esa-ui-library/lib/types';
import { AuthContext } from '../../../../logic';
import treeStyles from 'esa-ui-library/lib/components/TreeSelect/TreeSelect.module.scss';
import { TreeSelectOptions } from 'esa-ui-library/lib/components/TreeSelectOptions/TreeSelectOptions';
import { Chips } from 'esa-ui-library/lib/components/Chips/Chips';
import { ClickDetector } from 'esa-ui-library/lib/components/ClickDetector/ClickDetector';
import { ArrowDown } from 'esa-ui-library/lib/assets/icons';
import { groupBy } from 'lodash';

interface AssignGamesFormProps {
	onValidityChange?: (isValid: boolean) => void;
}

const AssignGamesForm = ({ onValidityChange }: AssignGamesFormProps): JSX.Element => {
	const [state, dispatch] = useStore();
	const [isLoading, setIsLoading] = useState(true);
	const { token } = useContext(AuthContext);
	const { globalSuccess, globalErrors, allGames, activeGames, partnerGames, selectedGamePartner } = state;
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [opened, setOpened] = useState<string[]>([]);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const getGames = async () => {
		if (!selectedGamePartner) return;
		setIsLoading(true);
		await dispatch('FETCH_PARTNER_GAMES', {
			token,
			partnerId: selectedGamePartner.skinid,
		});
		await dispatch('FETCH_ALL_GAMES', { token });
		setIsLoading(false);
	};

	const onGamesChange = (val: PropertyType[]) => {
		const flattened: PropertyType[] = [];

		// Identify which item was just toggled (added or removed)
		const currentValues = activeGames.map((g: PropertyType) => g.value);
		const newValues = val.map((v: PropertyType) => v.value);

		const added = val.find((v) => !currentValues.includes(v.value));
		const removed = activeGames.find((g: PropertyType) => !newValues.includes(g.value));

		const changedItem = added || removed;

		if (changedItem) {
			const group = gamesTree.find((g) => g.value === changedItem.value && g.options);
			if (group) {
				const groupOptionValues = group.options.map((opt: any) => opt.value);
				const allSelected = groupOptionValues.every((val: any) => currentValues.includes(val));

				let nextActiveGames: PropertyType[] = [...activeGames];

				if (allSelected) {
					// Clear all from this group
					nextActiveGames = nextActiveGames.filter((g) => !groupOptionValues.includes(g.value));
				} else {
					// Select all from this group
					group.options.forEach((opt: any) => {
						if (!nextActiveGames.find((f: any) => f.value === opt.value)) {
							nextActiveGames.push(opt);
						}
					});
				}

				dispatch('UPDATE_ACTIVE_GAMES', nextActiveGames);
				onValidityChange?.(nextActiveGames.length > 0);
				return;
			}
		}

		val.forEach((item: any) => {
			const group = gamesTree.find((g) => g.value === item.value && g.options);
			if (group) {
				group.options.forEach((opt: any) => {
					if (!flattened.find((f: any) => f.value === opt.value)) {
						flattened.push(opt);
					}
				});
			} else {
				if (!flattened.find((f: any) => f.value === item.value)) {
					flattened.push(item);
				}
			}
		});

		dispatch('UPDATE_ACTIVE_GAMES', flattened);
		onValidityChange?.(Array.isArray(flattened) && flattened.length > 0);
	};

	const onClear = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		dispatch('UPDATE_ACTIVE_GAMES', []);
		onValidityChange?.(false);
	};

	useEffect(() => {
		getGames();
	}, []);

	useEffect(() => {
		if (Array.isArray(partnerGames) && partnerGames.length > 0) {
			const preselected = partnerGames.map((g: any) => ({
				label: g.game_name,
				value: g.internal_game_id,
			}));

			dispatch('UPDATE_ACTIVE_GAMES', preselected);
			onValidityChange?.(true);
		} else {
			dispatch('UPDATE_ACTIVE_GAMES', []);
			onValidityChange?.(false);
		}
	}, [partnerGames]);

	const gamesTree = useMemo(() => {
		const grouped = groupBy(allGames, 'game_provider_name');

		return Object.keys(grouped).map((provider) => ({
			label: provider,
			value: provider,
			options: grouped[provider].map((g: any) => ({
				label: g.game_name,
				value: g.internal_game_id,
			})),
		}));
	}, [allGames]);

	const filteredGames = useMemo(() => {
		if (!searchTerm) return gamesTree;
		return searchNestedTree(gamesTree, searchTerm);
	}, [gamesTree, searchTerm]);

	const renderOptions = useMemo(
		() => (
			<section className={treeStyles.OptionWrapper}>
				<div className={styles.SearchContainer}>
					<Input
						type='text'
						value={searchTerm}
						onChange={setSearchTerm}
						icon={<Magnifier />}
						placeholder='Search games'
						small
					/>
					{searchTerm && <CloseRoundSmall className={styles.Clear} onClick={() => setSearchTerm('')} />}
				</div>
				<div className={styles.List}>
					<TreeSelectOptions
						options={filteredGames}
						selected={activeGames}
						opened={opened}
						onChange={onGamesChange}
						onOpenedChange={setOpened}
					/>
					{filteredGames?.length === 0 && <p className={styles.EmptyList}>No results</p>}
				</div>
			</section>
		),
		[filteredGames, activeGames, opened, searchTerm, onGamesChange]
	);

	return (
		<section className={isLoading ? styles.DisableForm : ''}>
			<Form success={globalSuccess} errors={globalErrors}>
				<div>
					<div className={`${treeStyles.TreeSelectContainer} ${styles.PartnerSelect}`}>
						<label className={treeStyles.Label}>
							Games
							<span className={treeStyles.Required}>*</span>
						</label>
						<ClickDetector
							onOutsideClick={() => {
								setIsOpen(false);
								setSearchTerm('');
							}}
							dataId='detector'
						>
							<div
								className={treeStyles.TreeSelectWrapper}
								onClick={() => !isOpen && setIsOpen(true)}
								data-testid='games-field'
							>
								<div className={`${treeStyles.TreeSelect} ${isOpen ? treeStyles.Active : ''}`}>
									{!!activeGames?.length && (
										<>
											<Chips
												options={activeGames as TreeValueType[]}
												onChange={onGamesChange}
												removeable
												primary
												limit={1}
											/>
											<div className={styles.Clear} onClick={onClear}>
												<CloseRoundSmall />
											</div>
										</>
									)}
									{!activeGames?.length && <span className={treeStyles.Placeholder}>Select games</span>}
									<i className={treeStyles.Icon}>
										<ArrowDown />
									</i>
								</div>
								{isOpen && (
									<div className={treeStyles.OptionContainer} data-testid='options'>
										{renderOptions}
									</div>
								)}
							</div>
						</ClickDetector>
					</div>
				</div>
			</Form>
		</section>
	);
};

export { AssignGamesForm };
